import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Home, Bed, Utensils, Bath, Tv, Heart, Shield, Calendar } from 'lucide-react';
import SEO from '../components/SEO';

const Logeeropvang = () => {
  const facilities = [
    { icon: Home, title: 'Eigen Woonkamer', description: 'Gezellige ruimte om te ontspannen en samen te zijn' },
    { icon: Bed, title: 'Comfortabele Slaapkamer', description: 'Rustige en veilige slaapplek voor een goede nachtrust' },
    { icon: Bath, title: 'Privé Badkamer', description: 'Eigen sanitaire voorzieningen voor privacy en comfort' },
    { icon: Utensils, title: 'Volledig Uitgeruste Keuken', description: 'Samen koken en eten in huiselijke sfeer' },
    { icon: Tv, title: 'Ontspanningsmogelijkheden', description: 'TV, spelletjes en andere recreatiemogelijkheden' },
    { icon: Heart, title: 'Huiselijke Sfeer', description: 'Warm en vertrouwd, zoals thuis' }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Logeeropvang Jeugd Nieuwstadt"
        description="Logeeropvang voor jeugd 3-18 jaar met beperking. Weekenden en vakanties. 24/7 begeleiding in huiselijke omgeving. Bel 06-19199023."
        keywords="logeeropvang, logeren, respijtzorg, aanleunwoning, weekend, vakantie, Nieuwstadt"
        
      />
      <Header />
      
      {/* Hero */}
      <section 
        className="pt-32 pb-16 bg-cover bg-center relative"
        style={{ 
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_skole-clone/artifacts/5hqxfkxg_IMG_8165%20%282%29.jpeg)',
          minHeight: '500px'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl">
            <h1 
              className="text-5xl md:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}
            >
              Logeeropvang
            </h1>
            <p className="text-xl text-white leading-relaxed mb-8">
              Een veilige en rustige slaapplek in onze aanleunwoning. Voor respijt, stabiliteit en een vertrouwde omgeving.
            </p>
            <a href="mailto:info@kscare.nl?subject=Aanmelding%20logeeropvang">
              <Button className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg">
                Aanmelden voor logeeropvang
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-[#1A4D4D] mb-6 text-center" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
              Onze Logeeropvang
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed text-center">
              Onze logeeropvang biedt een veilige en rustige slaapplek in een aanleunwoning met eigen faciliteiten. Met eigen woonkamer, slaapkamer en badkamer creëren wij een huiselijke en voorspelbare omgeving waar uw kind zich thuis voelt.
            </p>
          </div>

          {/* Facilities Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {facilities.map((facility, index) => {
              const Icon = facility.icon;
              return (
                <Card key={index} className="p-6 rounded-3xl border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E0F2F7] to-[#B3E5FC] rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-[#5DADE2]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#1A4D4D] mb-2">{facility.title}</h3>
                  <p className="text-gray-600">{facility.description}</p>
                </Card>
              );
            })}
          </div>

          {/* Features */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div>
              <img
                src="https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?w=800"
                alt="Aanleunwoning"
                className="rounded-3xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold text-[#1A4D4D] mb-6" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
                Speciaal Ingericht
              </h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-6">
                Onze aanleunwoning is speciaal ingericht voor logeeropvang. Alles is gericht op het creëren van een veilige, rustige en voorspelbare omgeving waar kinderen zich snel thuis voelen.
              </p>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <span>Veilige en kindvriendelijke inrichting</span>
                </li>
                <li className="flex items-start gap-3">
                  <Home className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <span>Huiselijke sfeer met warme begeleiding</span>
                </li>
                <li className="flex items-start gap-3">
                  <Heart className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                  <span>24/7 professionele zorg en aandacht</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Goals */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <h3 className="text-3xl font-bold text-[#1A4D4D] mb-12 text-center" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
            Doelen van Logeeropvang
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FFE8E0] to-[#FFD0C0] rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#FF6347]" />
              </div>
              <h4 className="text-xl font-bold text-[#1A4D4D] mb-3">Respijtzorg</h4>
              <p className="text-gray-600">Ontlasting en rust voor ouders en verzorgers</p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#E0F2F7] to-[#B3E5FC] rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#5DADE2]" />
              </div>
              <h4 className="text-xl font-bold text-[#1A4D4D] mb-3">Stabiliteit</h4>
              <p className="text-gray-600">Vaste structuur en voorspelbare routines</p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FFF4E0] to-[#FFE0B2] rounded-full flex items-center justify-center mx-auto mb-4">
                <Home className="w-8 h-8 text-[#F39C12]" />
              </div>
              <h4 className="text-xl font-bold text-[#1A4D4D] mb-3">Zelfstandigheid</h4>
              <p className="text-gray-600">Oefenen met zelfstandige vaardigheden</p>
            </div>

            <div className="bg-white p-8 rounded-3xl shadow-lg text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-[#52BE80]" />
              </div>
              <h4 className="text-xl font-bold text-[#1A4D4D] mb-3">Sociale Interactie</h4>
              <p className="text-gray-600">Samen zijn en sociale vaardigheden ontwikkelen</p>
            </div>
          </div>
        </div>
      </section>

      {/* Practical Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12">
            <h3 className="text-3xl font-bold text-[#1A4D4D] mb-8 text-center" style={{ fontFamily: 'Fredoka, Quicksand, sans-serif' }}>
              Praktische Informatie
            </h3>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h4 className="text-xl font-bold text-[#1A4D4D] mb-4">Wanneer</h4>
                <div className="space-y-3 text-gray-600">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                    <p>Weekenden (vrijdag t/m zondag)</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                    <p>Schoolvakanties</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#FF6347] mt-1 flex-shrink-0" />
                    <p>Doordeweeks (in overleg)</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-xl font-bold text-[#1A4D4D] mb-4">Wat meenemen</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                    Eigen kleding en toiletspullen
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                    Medicatie (indien nodig)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                    Favoriete knuffel of speelgoed
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FF6347] rounded-full"></span>
                    Alles voor een goede nachtrust
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 p-6 bg-[#FFF4E0] rounded-2xl">
              <p className="text-gray-700 text-center">
                <strong>Let op:</strong> Logeeropvang is mogelijk via WLZ, WMO en Jeugdwet financiering.
              </p>
            </div>
          </div>

          <div className="text-center mt-12">
            <a href="mailto:info@kscare.nl?subject=Kennismaking%20logeeropvang">
              <Button className="bg-[#FF6347] hover:bg-[#FF5533] text-white rounded-full px-8 py-6 text-lg">
                Plan een kennismaking
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Logeeropvang;